import { Box } from '@mui/material';

import { FBoxContainer, FHSimpleTable } from '../../base';
import {
  durationTrialAverageColumnNames,
  multiTargetPhaseChangeColumnNames,
  phaseChangeColumnNames,
  TREATMENT_PLAN_COLUMN_NAMES,
  trialAverageColumnNames,
} from './constants';
import { TPGoalDataCollectionMethod } from '../treatment-plan/goals/common/constants/general';
import { capitalizeFirstLetter } from '../../../helpers';
import { FORTA_SUCCESS_2 } from '../../../utils/colors';
import {
  DETAIL_CARD_SX,
  LOWER_CARD_SX,
  LOWER_PANELS_SX,
} from './styles';

import type { ITargetPhaseChangeTableProps } from './types';

function TargetPhaseChangeTable({
  dcm,
  durationUnit,
  rateUnit,
  hidePhaseChange = false,
  tableData = [],
  tableColumnNames = [],
  phaseChangeTableData = [],
  trialAverageTableData = [],
  durationTrialAverageTableData = [],
  selectedTargetsCount = 0,
  treatmentPlanTableData = [],
  showDetailTable = true,
  showLowerPanels = true,
  completedRunRows = [],
}: ITargetPhaseChangeTableProps) {
  const isSingleTarget = selectedTargetsCount === 1;
  const phaseColumns = isSingleTarget
    ? phaseChangeColumnNames
    : multiTargetPhaseChangeColumnNames;

  return (
    <>
      {isSingleTarget && showDetailTable && (
        <>
          <FBoxContainer
            dataTestId="chartData"
            sx={DETAIL_CARD_SX}
            contentStyle={{ p: 2 }}
          >
            <FHSimpleTable
              title={`${capitalizeFirstLetter(dcm?.toLowerCase() ?? '')} data ${
                dcm === TPGoalDataCollectionMethod.DURATION
                  ? `(in ${durationUnit}s)`
                  : dcm === TPGoalDataCollectionMethod.RATE
                    ? `(per ${rateUnit?.substring(0, rateUnit.length - 1)})`
                    : ''
              }`}
              data={tableData}
              columnDefinitions={tableColumnNames}
              columnOptions={{
                filter: false,
                print: false,
                search: false,
                viewColumns: false,
                selectableRows: 'none',
                tableBodyMaxHeight: '470px',
                pagination: false,
                download: false,
                responsive: 'standard',
                fixedHeader: false,
                sort: false,
                setRowProps: (_row: string[], dataIndex: number) =>
                  completedRunRows[dataIndex]
                    ? { style: { backgroundColor: FORTA_SUCCESS_2 } }
                    : {},
              }}
            />
          </FBoxContainer>
        </>
      )}

      {showLowerPanels && (
        <Box sx={LOWER_PANELS_SX}>
          <Box>
            <FBoxContainer sx={LOWER_CARD_SX} contentStyle={{ p: 2 }}>
              <FHSimpleTable
                title="TP(s) in timeframe"
                data={treatmentPlanTableData}
                columnDefinitions={TREATMENT_PLAN_COLUMN_NAMES}
                columnOptions={{
                  filter: false,
                  print: false,
                  search: false,
                  viewColumns: false,
                  selectableRows: 'none',
                  pagination: false,
                  download: false,
                  responsive: 'standard',
                  fixedHeader: false,
                  sort: false,
                }}
              />
            </FBoxContainer>
          </Box>
          <Box>
            {!hidePhaseChange && (
              <FBoxContainer sx={LOWER_CARD_SX} contentStyle={{ p: 2 }}>
                <FHSimpleTable
                  title="Phase Change Data"
                  data={phaseChangeTableData}
                  columnDefinitions={phaseColumns}
                  columnOptions={{
                    filter: false,
                    print: false,
                    search: false,
                    viewColumns: false,
                    selectableRows: 'none',
                    tableBodyMaxHeight: '700px',
                    pagination: false,
                    download: false,
                    responsive: 'standard',
                    fixedHeader: false,
                    sort: false,
                  }}
                />
              </FBoxContainer>
            )}
          </Box>
          <Box>
            {!hidePhaseChange && dcm === TPGoalDataCollectionMethod.DURATION ? (
              <FBoxContainer sx={LOWER_CARD_SX} contentStyle={{ p: 2 }}>
                <FHSimpleTable
                  title={`Target summary (in ${durationUnit}s)`}
                  data={durationTrialAverageTableData.map((row) => ({
                    target: row.target,
                    label: row.label,
                    average: row.average,
                    total: row.total,
                  }))}
                  columnDefinitions={durationTrialAverageColumnNames}
                  columnOptions={{
                    filter: false,
                    print: false,
                    search: false,
                    viewColumns: false,
                    selectableRows: 'none',
                    tableBodyMaxHeight: '700px',
                    pagination: false,
                    download: false,
                    responsive: 'standard',
                    fixedHeader: false,
                    sort: false,
                  }}
                />
              </FBoxContainer>
            ) : (
              <FBoxContainer sx={LOWER_CARD_SX} contentStyle={{ p: 2 }}>
                <FHSimpleTable
                  title={`Target summary (${capitalizeFirstLetter(dcm?.toLowerCase() ?? '')}${
                    dcm === TPGoalDataCollectionMethod.RATE
                      ? ` per ${rateUnit?.substring(0, rateUnit.length - 1)}`
                      : ''
                  })`}
                  data={trialAverageTableData.map((row) => ({
                    target: row.target,
                    label: row.label,
                    value: `${row.value}${row.unit === '%' ? '' : ' '}${row.unit}`,
                  }))}
                  columnDefinitions={trialAverageColumnNames}
                  columnOptions={{
                    filter: false,
                    print: false,
                    search: false,
                    viewColumns: false,
                    selectableRows: 'none',
                    tableBodyMaxHeight: '700px',
                    pagination: false,
                    download: false,
                    responsive: 'standard',
                    fixedHeader: false,
                    sort: false,
                  }}
                />
              </FBoxContainer>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

export default TargetPhaseChangeTable;
